import { useState } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  useGetNominalTemplates,
  useCreateNominalTemplate,
  useUpdateNominalTemplate,
  useDeleteNominalTemplate,
} from "@workspace/api-client-react";
import { Plus, Pencil, Trash2, Save, X, FileText } from "lucide-react";

interface NominalCode {
  name: string;
  code: string;
}

interface TemplateFormData {
  vendorPattern: string;
  promptTemplate: string;
  nominalCodes: NominalCode[];
}

const emptyForm: TemplateFormData = {
  vendorPattern: "",
  promptTemplate: "",
  nominalCodes: [{ name: "", code: "" }],
};

export default function NominalTemplatesPage() {
  const { data: templates, isLoading, refetch } = useGetNominalTemplates();
  const createMutation = useCreateNominalTemplate();
  const updateMutation = useUpdateNominalTemplate();
  const deleteMutation = useDeleteNominalTemplate();
  const { toast } = useToast();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<TemplateFormData>({ ...emptyForm });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<TemplateFormData>({ ...emptyForm });

  const handleCreate = () => {
    if (!createForm.vendorPattern || !createForm.promptTemplate) {
      toast({ title: "Error", description: "Vendor pattern and prompt template are required", variant: "destructive" });
      return;
    }
    const validCodes = createForm.nominalCodes.filter((nc) => nc.name && nc.code);
    createMutation.mutate(
      { data: { ...createForm, nominalCodes: validCodes } },
      {
        onSuccess: () => {
          toast({ title: "Template created" });
          setShowCreateForm(false);
          setCreateForm({ ...emptyForm });
          refetch();
        },
        onError: () => toast({ title: "Error", description: "Failed to create template", variant: "destructive" }),
      }
    );
  };

  const handleUpdate = (id: number) => {
    const validCodes = editForm.nominalCodes.filter((nc) => nc.name && nc.code);
    updateMutation.mutate(
      { id, data: { ...editForm, nominalCodes: validCodes } },
      {
        onSuccess: () => {
          toast({ title: "Template updated" });
          setEditingId(null);
          refetch();
        },
        onError: () => toast({ title: "Error", description: "Failed to update template", variant: "destructive" }),
      }
    );
  };

  const handleDelete = (id: number) => {
    if (!confirm("Delete this nominal template?")) return;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          toast({ title: "Template deleted" });
          refetch();
        },
      }
    );
  };

  const startEdit = (template: any) => {
    setEditingId(template.id);
    setEditForm({
      vendorPattern: template.vendorPattern,
      promptTemplate: template.promptTemplate,
      nominalCodes: (template.nominalCodes as NominalCode[])?.length > 0
        ? (template.nominalCodes as NominalCode[])
        : [{ name: "", code: "" }],
    });
  };

  const renderNominalCodesEditor = (
    codes: NominalCode[],
    onChange: (codes: NominalCode[]) => void
  ) => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">Nominal Codes</label>
      {codes.map((nc, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input
            type="text"
            placeholder="Category name (e.g. PSA)"
            value={nc.name}
            onChange={(e) => {
              const updated = [...codes];
              updated[i] = { ...updated[i], name: e.target.value };
              onChange(updated);
            }}
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <input
            type="text"
            placeholder="Code (e.g. 52405)"
            value={nc.code}
            onChange={(e) => {
              const updated = [...codes];
              updated[i] = { ...updated[i], code: e.target.value };
              onChange(updated);
            }}
            className="w-32 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const updated = codes.filter((_, j) => j !== i);
              onChange(updated.length > 0 ? updated : [{ name: "", code: "" }]);
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...codes, { name: "", code: "" }])}
      >
        <Plus className="w-3 h-3 mr-1" /> Add Code
      </Button>
    </div>
  );

  const renderForm = (
    form: TemplateFormData,
    setForm: (f: TemplateFormData) => void,
    onSave: () => void,
    onCancel: () => void,
    saving: boolean,
  ) => (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="p-6 space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">Vendor Pattern</label>
          <p className="text-xs text-slate-500 mb-1">The vendor name or keyword to match receipts against (e.g. "Asda", "Amazon")</p>
          <input
            type="text"
            placeholder="e.g. Asda"
            value={form.vendorPattern}
            onChange={(e) => setForm({ ...form, vendorPattern: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700">Breakdown Prompt</label>
          <p className="text-xs text-slate-500 mb-1">Instructions for how AI should categorize the receipt line items</p>
          <textarea
            placeholder={`e.g. Group and total line items such as sugar, tea, milk, coffee, and any condiments and label as "Staff PSA".\n\nGroup and total values for line items such as fruit and biscuits and label as "Staff fruit".\n\nAny items remaining please group and total as staff welfare.\n\nIf there is delivery/carriage, please add that in its own category.`}
            value={form.promptTemplate}
            onChange={(e) => setForm({ ...form, promptTemplate: e.target.value })}
            rows={8}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
          />
        </div>

        {renderNominalCodesEditor(form.nominalCodes, (codes) =>
          setForm({ ...form, nominalCodes: codes })
        )}

        <div className="flex gap-2 pt-2">
          <Button onClick={onSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Template"}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return <Layout><div className="p-8 text-center">Loading nominal templates...</div></Layout>;
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900">Nominal Code Templates</h1>
          <p className="text-slate-500 mt-1">
            Define how receipts from specific vendors should be broken down into nominal codes using AI
          </p>
        </div>
        {!showCreateForm && (
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        )}
      </div>

      {showCreateForm && renderForm(
        createForm,
        setCreateForm,
        handleCreate,
        () => { setShowCreateForm(false); setCreateForm({ ...emptyForm }); },
        createMutation.isPending,
      )}

      <div className="space-y-4 mt-4">
        {(!templates || templates.length === 0) && !showCreateForm && (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No nominal templates yet</h3>
              <p className="text-slate-500 mb-4">
                Create templates to define how specific vendor receipts should be broken down into nominal codes
              </p>
              <Button onClick={() => setShowCreateForm(true)}>
                <Plus className="w-4 h-4 mr-2" /> Create First Template
              </Button>
            </CardContent>
          </Card>
        )}

        {templates?.map((template: any) => (
          <div key={template.id}>
            {editingId === template.id ? (
              renderForm(
                editForm,
                setEditForm,
                () => handleUpdate(template.id),
                () => setEditingId(null),
                updateMutation.isPending,
              )
            ) : (
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-semibold text-slate-900">{template.vendorPattern}</h3>
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                          {(template.nominalCodes as NominalCode[])?.length || 0} codes
                        </span>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-4 mb-3">
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{template.promptTemplate}</p>
                      </div>

                      {(template.nominalCodes as NominalCode[])?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {(template.nominalCodes as NominalCode[]).map((nc: NominalCode, i: number) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full"
                            >
                              <span className="font-medium">{nc.name}</span>
                              <span className="text-blue-400">-</span>
                              <span className="font-mono">{nc.code}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-1 ml-4 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(template)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
